import { memo, useEffect, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import type { DrivingEvent } from "../utils/drivingScore";
import { escapeHtml } from "../../../utils/escapeHtml";

// Mapa de Comportamiento del conductor (como Mapon/Wialon, min 42-44 del video
// del 26-oct): la ruta de la unidad y cada violación numerada con su color.
const GOOGLE_MAPS_LIBRARIES: ("places" | "drawing" | "geometry")[] = ["places"];
const DEFAULT_CENTER = { lat: 23.6345, lng: -102.5528 };
// Más puntos que esto no se ven mejor y vuelven lenta la polilínea (un día
// tiene ~1 440 a un punto por minuto; 31 días, ~45 000).
const MAX_ROUTE_POINTS = 4000;

export const KIND_COLOR: Record<DrivingEvent["kind"], string> = {
  harshBrake: "#DC2626",
  harshAccel: "#EA580C",
  harshTurn: "#9333EA",
  speeding: "#D97706",
  idle: "#2563EB",
};

interface Props {
  route: { lat: number; lng: number }[];
  events: (DrivingEvent & { label: string; when: string })[];
  selectedTs?: number | null;
  height?: number;
}

const thin = <T,>(arr: T[], max: number): T[] => {
  if (arr.length <= max) return arr;
  const step = arr.length / max;
  return Array.from({ length: max }, (_, i) => arr[Math.floor(i * step)]).concat(arr[arr.length - 1]);
};

const BehaviorEventsMap = memo(({ route, events, selectedTs = null, height = 380 }: Props) => {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY, libraries: GOOGLE_MAPS_LIBRARIES });
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markers = useRef(new Map<number, google.maps.Marker>());
  const info = useRef<google.maps.InfoWindow | null>(null);

  useEffect(() => {
    if (!map) return;
    const path = thin(route.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)), MAX_ROUTE_POINTS);
    const line = new google.maps.Polyline({ path, map, strokeColor: "#475569", strokeOpacity: 0.8, strokeWeight: 3 });
    info.current = new google.maps.InfoWindow();
    const made = events.map((e, i) => {
      const m = new google.maps.Marker({
        position: { lat: e.lat, lng: e.lng },
        map,
        zIndex: 5,
        label: { text: String(i + 1), color: "#ffffff", fontSize: "10px", fontWeight: "700" },
        icon: { path: google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: KIND_COLOR[e.kind], fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2 },
        title: `${i + 1}. ${e.label}`,
      });
      m.addListener("click", () => {
        info.current?.setContent(`<div class="report-map-popup report-map-popup--pinned"><div class="report-map-popup__title">${escapeHtml(e.when)}</div><div>${escapeHtml(e.label)}</div></div>`);
        info.current?.open({ map, anchor: m });
      });
      markers.current.set(e.ts, m);
      return m;
    });
    const bounds = new google.maps.LatLngBounds();
    (path.length ? path : events).forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    if (!bounds.isEmpty()) map.fitBounds(bounds, 48);
    return () => {
      line.setMap(null);
      made.forEach((m) => m.setMap(null));
      markers.current.clear();
      info.current?.close();
    };
  }, [map, route, events]);

  useEffect(() => {
    if (!map || selectedTs == null) return;
    const m = markers.current.get(selectedTs);
    if (!m) return;
    map.panTo(m.getPosition()!);
    if ((map.getZoom() ?? 5) < 15) map.setZoom(16);
    google.maps.event.trigger(m, "click");
  }, [map, selectedTs]);

  if (!isLoaded) return <div className="w-full flex items-center justify-center text-sm text-gray-500" style={{ height }}>Cargando mapa…</div>;
  return (
    <GoogleMap
      mapContainerStyle={{ width: "100%", height: `${height}px`, borderRadius: "0.5rem" }}
      center={DEFAULT_CENTER}
      zoom={5}
      onLoad={(m) => setMap(m)}
      onUnmount={() => setMap(null)}
      options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true, clickableIcons: false, gestureHandling: "greedy" }}
    />
  );
});

export default BehaviorEventsMap;
